import * as React from "react";
import { useLocation } from "react-router-dom";
import {
  IS_HEADLESS,
  STORAGE_KEY_GUIDE_ACCOUNT_ORG,
  STORAGE_KEY_GUIDE_CONTEXT,
  STORAGE_KEY_GUIDE_LEGACY,
  STORAGE_KEY_GUIDE_LIMIT_BY_CONTEXT,
} from "../envVariables";
import { Legacy } from "./legacy";
import { Context } from "./context";
import { AccountOrg } from "./accountOrg";
import { LimitByContext } from "./limitByContext";

export type GuideKey = "legacy" | "context" | "accountOrg" | "limitByContext";

export const GUIDE_ORDER: GuideKey[] = ["legacy", "context", "accountOrg", "limitByContext"];

export const GUIDE_NAMES: Record<GuideKey, string> = {
  legacy: "Switch to Legacy UI",
  context: "Context Selection",
  accountOrg: "Organization Management",
  limitByContext: "Filter by GVC",
};

const GUIDE_STORAGE_KEYS: Record<GuideKey, string> = {
  legacy: STORAGE_KEY_GUIDE_LEGACY,
  context: STORAGE_KEY_GUIDE_CONTEXT,
  accountOrg: STORAGE_KEY_GUIDE_ACCOUNT_ORG,
  limitByContext: STORAGE_KEY_GUIDE_LIMIT_BY_CONTEXT,
};

export const GUIDE_RESET_EVENT = "guide-reset";
export const GUIDE_RESET_ALL_EVENT = "guide-reset-all";
export const GUIDE_SKIP_ALL_EVENT = "guide-skip-all";

export interface GuideProps {
  onDone: () => void;
  onSkip: () => void;
  currentStep: number;
  totalSteps: number;
}

export const Guides: React.FC = () => {
  const { pathname } = useLocation();
  const [legacy, setLegacy] = React.useState(localStorage.getItem(STORAGE_KEY_GUIDE_LEGACY || "false") === "true");
  const [context, setContext] = React.useState(localStorage.getItem(STORAGE_KEY_GUIDE_CONTEXT || "false") === "true");
  const [accountOrg, setAccountOrg] = React.useState(
    localStorage.getItem(STORAGE_KEY_GUIDE_ACCOUNT_ORG || "false") === "true",
  );
  const [limitByContext, setLimitByContext] = React.useState(
    localStorage.getItem(STORAGE_KEY_GUIDE_LIMIT_BY_CONTEXT || "false") === "true",
  );

  const guideStates = { legacy, context, accountOrg, limitByContext };
  const guideSetters = {
    legacy: setLegacy,
    context: setContext,
    accountOrg: setAccountOrg,
    limitByContext: setLimitByContext,
  };

  React.useEffect(() => {
    const handleReset = (event: CustomEvent<GuideKey>) => {
      const guideKey = event.detail;
      guideSetters[guideKey](false);
    };

    const handleResetAll = () => {
      setLegacy(false);
      setContext(false);
      setAccountOrg(false);
      setLimitByContext(false);
    };

    const handleSkipAll = () => {
      GUIDE_ORDER.forEach((key) => {
        localStorage.setItem(GUIDE_STORAGE_KEYS[key], "true");
      });
      setLegacy(true);
      setContext(true);
      setAccountOrg(true);
      setLimitByContext(true);
    };

    window.addEventListener(GUIDE_RESET_EVENT, handleReset as EventListener);
    window.addEventListener(GUIDE_RESET_ALL_EVENT, handleResetAll);
    window.addEventListener(GUIDE_SKIP_ALL_EVENT, handleSkipAll);
    return () => {
      window.removeEventListener(GUIDE_RESET_EVENT, handleReset as EventListener);
      window.removeEventListener(GUIDE_RESET_ALL_EVENT, handleResetAll);
      window.removeEventListener(GUIDE_SKIP_ALL_EVENT, handleSkipAll);
    };
  }, []);

  React.useEffect(() => {
    if (legacy) {
      localStorage.setItem(STORAGE_KEY_GUIDE_LEGACY, "true");
    }
  }, [legacy]);

  React.useEffect(() => {
    if (context) {
      localStorage.setItem(STORAGE_KEY_GUIDE_CONTEXT, "true");
    }
  }, [context]);

  React.useEffect(() => {
    if (accountOrg) {
      localStorage.setItem(STORAGE_KEY_GUIDE_ACCOUNT_ORG, "true");
    }
  }, [accountOrg]);

  React.useEffect(() => {
    if (limitByContext) {
      localStorage.setItem(STORAGE_KEY_GUIDE_LIMIT_BY_CONTEXT, "true");
    }
  }, [limitByContext]);

  if (IS_HEADLESS) {
    return null;
  }

  const isInConsole = pathname.includes("/console");
  const isInCreatePage = pathname.includes("-create");
  const isInSelectPage = pathname.includes("-select");

  if (!isInConsole || isInCreatePage || isInSelectPage) {
    return null;
  }

  const totalSteps = GUIDE_ORDER.length;
  const completedCount = GUIDE_ORDER.filter((key) => guideStates[key]).length;
  const currentStep = completedCount + 1;

  const skipAll = () => {
    window.dispatchEvent(new CustomEvent(GUIDE_SKIP_ALL_EVENT));
  };

  if (!legacy) {
    return <Legacy onDone={() => setLegacy(true)} onSkip={skipAll} currentStep={currentStep} totalSteps={totalSteps} />;
  }

  if (!context) {
    return <Context onDone={() => setContext(true)} onSkip={skipAll} currentStep={currentStep} totalSteps={totalSteps} />;
  }

  if (!accountOrg) {
    return <AccountOrg onDone={() => setAccountOrg(true)} onSkip={skipAll} currentStep={currentStep} totalSteps={totalSteps} />;
  }

  // Only show limitByContext guide on workload, identity, and volumeset list pages
  // Must check pathname ends with resource name to exclude detail pages
  const pathWithoutTrailingSlash = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  const isOnLimitByContextPage =
    pathWithoutTrailingSlash.endsWith("/workload") ||
    pathWithoutTrailingSlash.endsWith("/identity") ||
    pathWithoutTrailingSlash.endsWith("/volumeset");

  if (!limitByContext && isOnLimitByContextPage) {
    return <LimitByContext onDone={() => setLimitByContext(true)} onSkip={skipAll} currentStep={currentStep} totalSteps={totalSteps} />;
  }

  return null;
};

export function resetGuide(guideKey: GuideKey) {
  localStorage.setItem(GUIDE_STORAGE_KEYS[guideKey], "false");
  window.dispatchEvent(new CustomEvent(GUIDE_RESET_EVENT, { detail: guideKey }));
}

export function resetAllGuides() {
  GUIDE_ORDER.forEach((key) => {
    localStorage.setItem(GUIDE_STORAGE_KEYS[key], "false");
  });
  window.dispatchEvent(new CustomEvent(GUIDE_RESET_ALL_EVENT));
}

export function getGuideKeys(): GuideKey[] {
  return [...GUIDE_ORDER];
}
